/*
 * Decléor — promo codes in the cart, with a gift attached to a code
 * ---------------------------------------------------------------------------
 * A visitor types a code in the cart. This file applies it to the cart, tells them whether it landed,
 * and — if the merchant attached a product to that code — adds that product too.
 *
 * EVERYTHING BELOW IS BUILT ON FOUR THINGS MEASURED ON THIS STORE, not on documentation.
 * Run against decleor.com with a real 348.50 EUR cart:
 *
 *   POST /cart/update.js {"discount":"BEAUTY20"}
 *     -> 200, discount_codes: [{code:"BEAUTY20", applicable:true}], total_discount 6970
 *   POST /cart/update.js {"discount":"NEROLI10"}
 *     -> 200, discount_codes: [{code:"NEROLI10", applicable:false}], total_discount 0
 *   POST /cart/update.js {"discount":"ZZZNOPE"}          (a code that does not exist)
 *     -> 200, discount_codes: [{code:"ZZZNOPE", applicable:false}]
 *   POST /cart/update.js {"discount":"BEAUTY20,ML001"}
 *     -> 200, [{BEAUTY20, applicable:false}, {ML001, applicable:true}]
 *
 * Four consequences, and they shape every decision in this file:
 *
 * 1. A BAD CODE IS NOT AN ERROR. The endpoint answers 200 and simply flags the code
 *    `applicable: false`. There is no exception to catch and no status code to branch on — the ONLY way
 *    to know whether a code worked is to read the flag back out of the response. Anything that assumes a
 *    rejected code throws will report success on a typo.
 *
 * 2. THE PARAMETER REPLACES THE WHOLE LIST. Sending one code does not append it; it becomes the entire
 *    set (verified: sending "ML001" after "BEAUTY20,ML001" left ML001 alone on the cart, and sending ""
 *    emptied it). So every write is read-modify-write, or adding a second code silently discards the
 *    first.
 *
 * 3. SHOPIFY ALREADY ARBITRATES STACKING, AND REPORTS WHO LOST. In the fourth call above Shopify kept
 *    ML001 and marked BEAUTY20 inapplicable, because those two discounts' `combinesWith` do not permit
 *    the pair. That arbitration is authoritative — it is the same engine checkout runs — so this file
 *    does not reimplement it. It reads the answer and explains it. What it cannot do is express a
 *    PER-PAIR rule: `combinesWith` is per class (order / product / shipping), so "A stacks with B but not
 *    with C", where B and C are both product discounts, has no native expression.
 *
 * 4. LA VÉRIFICATION EST LE FILET, PAS L'ORDRE. Le code est tenté SEUL d'abord et son drapeau
 *    `applicable` est lu avant tout ajout. Mais une remise « Achetez X, obtenez Y » n'est applicable que
 *    si le produit offert est déjà au panier : dans ce cas settle() pose la ligne et réessaie une fois,
 *    puis la retire si le code ne prend toujours pas. Quel que soit l'ordre, verifyGiftFree() tourne en
 *    DERNIER et retire toute ligne annoncée offerte qui n'est pas tombée à zéro. C'est elle qui ferme le
 *    risque « article affiché offert et facturé », pas la séquence.
 *
 * REFRESHING THE CART is the theme's own job, through its own event. `theme:cart:reload` (dispatched on
 * document, bubbles) is what theme.js listens to: the drawer sets itself stale and calls loadHTML(), and
 * the cart page section calls window.location.reload(). That page reload is why a message is stashed in
 * sessionStorage before dispatching — otherwise the outcome the visitor needs to read is wiped a frame
 * after it appears. `theme:cart:change` goes out first with the fresh cart object, which is what
 * cart-count-badge.js, cart-fix-total.js, cart-reward-ladder.js and cart-truth-reconciler.js listen to.
 *
 * The form markup is snippets/cart-promo-form.liquid and the merchant's rules are emitted as JSON by
 * sections/cart-drawer.liquid. No-op on any page with no [data-cart-promo].
 */
(function () {
  'use strict';

  /*
   * GARDE CÔTÉ THÈME, PAS UNE RÈGLE DE L'API — la nuance compte pour qui reprendra ce fichier.
   *
   * Mesuré : /cart/update.js avale SEPT codes sans broncher. HTTP 200, les sept rendus dans
   * `discount_codes`, un seul marqué `applicable: true`. L'endpoint ne plafonne rien.
   *
   * Le 5 est le maximum que Shopify documente par commande au checkout. Je ne l'ai PAS vérifié ici :
   * il faudrait six remises cumulables entre elles, et cette boutique n'en a pas. C'est donc une valeur
   * prudente, pas une mesure.
   *
   * Sans ce garde, le visiteur empilerait des codes qui ne remisent rien et repartirait avec une liste
   * de pastilles inertes.
   */
  var MAX_CODES = 5;

  /* The line property that marks a line as belonging to a promo code. Underscore-prefixed, which is
     Shopify's convention for a property hidden from the customer, the order confirmation and the packing
     slip — the visitor should see "Offert", not a machine tag. It is also how a gift is found again when
     its code is removed. */
  var GIFT_PROP = '_promo_gift';

  var STASH = 'dc-cart-promo-message';

  var config = null;
  var busy = false;

  /* A real Error, not a bare object: a thrown plain object has no stack, and anything downstream testing
     `instanceof Error` — a logger, an error reporter, a future try/catch — would misread it as a value
     rather than a failure. `soft` marks the ones whose message is meant for the visitor. */
  function softError(text) {
    var failure = new Error(text);
    failure.soft = text;
    return failure;
  }

  /* ---------------------------------------------------------------- config */

  function readConfig() {
    var node = document.querySelector('[data-cart-promo-config]');
    if (!node) {
      return { rules: [], enforce_pairs: false };
    }
    try {
      var parsed = JSON.parse(node.textContent);
      return {
        rules: parsed && parsed.rules ? parsed.rules : [],
        enforce_pairs: !!(parsed && parsed.enforce_pairs),
      };
    } catch (e) {
      /* A malformed config must not take the code field down with it: the field is still useful without
         any gift attached to any code. */
      return { rules: [], enforce_pairs: false };
    }
  }

  function norm(code) {
    return String(code == null ? '' : code).trim().toUpperCase();
  }

  function rules() {
    if (!config) {
      config = readConfig();
    }
    return config;
  }

  function ruleFor(code) {
    var want = norm(code);
    var i;
    var list = rules().rules;
    for (i = 0; i < list.length; i += 1) {
      if (norm(list[i].code) === want) {
        return list[i];
      }
    }
    return null;
  }

  /* ------------------------------------------------------------------ i18n */

  function t(key, fallback, vars) {
    var node = document.querySelector('[data-cart-promo-strings]');
    var out = fallback;
    if (node) {
      try {
        var dict = JSON.parse(node.textContent);
        if (dict && typeof dict[key] === 'string' && dict[key]) {
          out = dict[key];
        }
      } catch (e) {
        /* fall through to the English fallback */
      }
    }
    /* Placeholders are [code] / [a] / [b] / [n], NOT Liquid's {{ }}. Shopify's `t` filter interpolates
       {{ }} inside a translation itself, so a placeholder written that way is consumed before this file
       ever sees the string — the message would reach the visitor with the code missing. Square brackets
       pass through `t` untouched. */
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        out = out.split('[' + k + ']').join(vars[k]);
      });
    }
    return out;
  }

  /* ------------------------------------------------------------------- net */

  /*
   * UNE ECRITURE REFUSEE DOIT REJETER, PAS RESOUDRE.
   *
   * Les endpoints panier de Shopify repondent a leurs erreurs par un 4xx portant un corps JSON
   * {status, message, description}. Sans le test ci-dessous, `r.json()` resolvait pareil dans les deux
   * cas : un /cart/change.js refuse — 422 « Cart Error », typiquement quand une app touche le panier au
   * meme instant — etait indiscernable d'une suppression reussie. verifyGiftFree annoncait alors le
   * retrait d'une ligne toujours presente, et verrouillee, donc insupprimable par le client.
   *
   * Le corps est garde dans `err.body` : c'est la que Shopify explique ce qu'il a refuse.
   */
  function asJson(r) {
    return r.json().then(function (body) {
      if (r.ok) {
        return body;
      }
      var err = new Error('cart ' + r.status);
      err.body = body;
      throw err;
    });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(asJson);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(asJson);
  }

  /* Codes currently on the cart. Only the ones that actually APPLIED are carried forward: an inapplicable
     code is dead weight that would keep occupying one of the five slots and would keep being re-sent on
     every subsequent write. */
  function liveCodes(cart) {
    var out = [];
    var list = (cart && cart.discount_codes) || [];
    var i;
    for (i = 0; i < list.length; i += 1) {
      if (list[i].applicable) {
        out.push(list[i].code);
      }
    }
    return out;
  }

  function setCodes(codes) {
    /* An empty list must still be sent as "" — that is what clears the cart's codes (verified). Sending
       nothing at all would leave the previous list untouched. */
    return post('/cart/update.js', { discount: codes.join(',') });
  }

  /* ----------------------------------------------------------------- gifts */

  function giftLine(cart, code) {
    var want = norm(code);
    var items = (cart && cart.items) || [];
    var i;
    for (i = 0; i < items.length; i += 1) {
      var props = items[i].properties;
      if (props && norm(props[GIFT_PROP]) === want) {
        return items[i];
      }
    }
    return null;
  }

  function removeLine(line) {
    /* `id` here is the line KEY, not the variant id: two lines can share a variant (different properties)
       and quantity-by-variant would remove the wrong one — or both. */
    return post('/cart/change.js', { id: line.key, quantity: 0 });
  }

  /*
   * AJOUT ET VÉRIFICATION SONT SÉPARÉS, ET C'EST TOUT L'INTÉRÊT.
   *
   * Selon la remise que le marchand a créée, le code et la ligne cadeau n'arrivent pas dans le même
   * ordre :
   *
   *   • une remise qui vaut par elle-même (-20 % panier) est applicable sur un panier sans le cadeau,
   *     donc le code passe d'abord et la ligne suit ;
   *   • une remise « Achetez X, obtenez Y » ne vaut QUE par le produit offert. Tant qu'il n'est pas au
   *     panier, Shopify répond `applicable: false` — mesuré sur NEROLI10, qui ne cible que deux
   *     produits : false sans eux, true avec. Il faut donc poser la ligne AVANT de réessayer le code.
   *
   * La garantie « rien d'affiché comme offert n'est facturé » ne vient pas de l'ordre, elle vient de
   * verifyGiftFree(), qui tourne toujours en DERNIER, une fois le code appliqué. L'ordre n'est plus
   * qu'une question de ce que Shopify accepte.
   */
  function addGiftLine(rule, code) {
    if (!rule || !rule.variant_id) {
      return Promise.resolve({ added: false });
    }

    return getCart().then(function (cart) {
      /* Déjà là — deuxième envoi du même code, essai précédent, ou panier d'un visiteur qui revient.
         Réajouter en donnerait deux. */
      if (giftLine(cart, code)) {
        return { added: false, already: true };
      }

      var props = {};
      props[GIFT_PROP] = norm(code);

      return post('/cart/add.js', {
        items: [{ id: rule.variant_id, quantity: 1, properties: props }],
      }).then(function (res) {
        /* add.js signale ses échecs, contrairement à update.js : une variante épuisée ou dépubliée
           revient avec un `status` et une `description` au lieu d'une ligne. */
        if (res && res.status && res.status >= 400) {
          return { added: false, unavailable: true };
        }
        return getCart().then(function (fresh) {
          var line = giftLine(fresh, code);
          return line ? { added: true, line: line } : { added: false, unavailable: true };
        });
      });
    });
  }

  /*
   * Le filet. Un code qui s'applique et un cadeau qui ne coûte rien sont deux faits distincts : un code
   * -20 % panier s'applique très bien et laisse un « cadeau » à 36 EUR à 28,80 EUR. Seule une variante à
   * 0 EUR ou une remise construite pour annuler ce produit le rend gratuit, et rien ici ne peut le
   * savoir à l'avance. Donc on relit, et on retire la ligne si elle n'est pas tombée à zéro. Le visiteur
   * lit un message au lieu de recevoir une facture.
   */
  function verifyGiftFree(rule, code) {
    if (!rule || !rule.must_be_free) {
      return Promise.resolve({ ok: true });
    }
    return getCart().then(function (cart) {
      var line = giftLine(cart, code);
      if (!line) {
        return { ok: true, absent: true };
      }
      if (line.final_line_price > 0) {
        return removeLine(line).then(function () {
          return { ok: false, not_free: true };
        });
      }
      return { ok: true };
    });
  }

  /* Remove the gift that belongs to a code, used when its code is taken off the cart. */
  function dropGift(code) {
    return getCart().then(function (cart) {
      var line = giftLine(cart, code);
      return line ? removeLine(line) : cart;
    });
  }

  /* -------------------------------------------------------------- gardiens */

  var normalising = false;

  /*
   * UNE LIGNE CADEAU VAUT EXACTEMENT 1, ET IL N'Y EN A QU'UNE PAR CODE.
   *
   * Mesuré sur un panier réel, avant ce garde : le stepper de la ligne cadeau était visible, un clic sur
   * « + » envoyait `change.js {quantity: 2}`, et comme une remise « Achetez X, obtenez Y » n'offre qu'UNE
   * unité, Shopify scindait la ligne en deux exemplaires à 36 EUR en déplaçant la gratuité sur la crème
   * que le client avait payée. Trois lignes du même produit, dont deux facturées sous l'étiquette
   * « offert ».
   *
   * snippets/cart-line-items.liquid masque désormais ce stepper, mais un masquage CSS n'est pas une
   * règle : un POST /cart/change.js direct passe à travers, et c'est une note laissée dans ce dépôt
   * depuis longtemps. Ceci est la règle.
   *
   * Tourne sur theme:cart:change, donc après TOUTE mutation du panier, d'où qu'elle vienne — le thème,
   * une app, ou la console du visiteur.
   */
  function normaliseGifts(cart) {
    /* `busy` autant que `normalising` : pendant un apply(), la ligne est posée AVANT que le code ne
       prenne (le réessai de settle()). Normaliser à cet instant la retirerait aussitôt. */
    if (normalising || busy || !(cart && cart.items)) {
      return;
    }

    /* Les codes qui remisent VRAIMENT quelque chose en ce moment. Un code présent mais `applicable:
       false` ne compte pas : il ne paie plus le cadeau. */
    var live = {};
    var codes = cart.discount_codes || [];
    var n;
    for (n = 0; n < codes.length; n += 1) {
      if (codes[n].applicable) {
        live[norm(codes[n].code)] = true;
      }
    }

    var seen = {};
    var fixes = [];
    var i;
    for (i = 0; i < cart.items.length; i += 1) {
      var item = cart.items[i];
      var code = item.properties ? item.properties[GIFT_PROP] : null;
      if (!code) {
        continue;
      }
      var key = norm(code);

      /* ORPHELIN : la ligne cadeau est là, son code n'y est plus. Le client le retire par la pastille,
         Shopify l'évince au profit d'un autre, sa date de fin passe, son marché change — le cadeau n'est
         plus payé par rien et se retrouve à plein tarif. Et depuis que la ligne est verrouillée, le
         client n'a même plus de bouton pour s'en débarrasser : observé sur un panier réel, une CRÈME
         YEUX JASMIN à 36 EUR figée, sans quantité ni poubelle. La ligne part avec son code. */
      if (!live[key]) {
        fixes.push({ id: item.key, quantity: 0 });
        continue;
      }

      if (seen[key]) {
        /* Deuxième ligne pour le même code : c'est un éclat, pas un deuxième cadeau. */
        fixes.push({ id: item.key, quantity: 0 });
      } else {
        seen[key] = true;
        if (item.quantity !== 1) {
          fixes.push({ id: item.key, quantity: 1 });
        }
      }
    }
    if (!fixes.length) {
      return;
    }

    normalising = true;
    /* Troisième fenêtre, hors de setBusy : cette chaîne retire des lignes cadeaux ORPHELINES, donc à
       plein tarif, une requête à la fois. Partir au checkout au milieu, c'est les payer. */
    holdCheckout(true);
    var chain = Promise.resolve();
    fixes.forEach(function (fix) {
      chain = chain.then(function () {
        return post('/cart/change.js', fix);
      });
    });
    chain
      .then(getCart)
      .then(function (fresh) {
        normalising = false;
        holdCheckout(false);
        /* On réémet pour que le reste du thème reparte de la vérité, mais sans repasser par announce() :
           aucun message n'est dû au visiteur, il n'a fait que tenter un geste qui n'a pas de sens. */
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', { detail: { cart: fresh }, bubbles: true })
        );
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      })
      .catch(function () {
        normalising = false;
        holdCheckout(false);
      });
  }

  /* ---------------------------------------------------------------- render */

  function each(selector, fn) {
    var nodes = document.querySelectorAll(selector);
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      fn(nodes[i]);
    }
  }

  /*
   * `quiet` = annoncé, pas affiché.
   *
   * "BEAUTY20 appliqué." et "BEAUTY20 retiré." répètent une information que la pastille donne déjà, en
   * mieux : elle reste à l'écran et porte un bouton pour défaire. La phrase n'ajoutait qu'une ligne de
   * bruit sous un champ déjà chargé.
   *
   * Elle est donc masquée VISUELLEMENT et non supprimée : le <p> est une région aria-live, et la
   * pastille qui apparaît ou disparaît ne produit aucune annonce. La retirer priverait un lecteur
   * d'écran du seul retour qu'il reçoit. `.visually-hidden` (theme.css) sort l'élément du flux, donc il
   * ne prend plus aucune place non plus.
   *
   * Les messages qui apprennent quelque chose — erreur, non-cumulable, cadeau épuisé, cadeau ajouté —
   * restent visibles.
   */
  function message(text, tone, quiet) {
    each('[data-cart-promo-message]', function (el) {
      el.textContent = text || '';
      el.classList.toggle('visually-hidden', !!(text && quiet));
      if (text) {
        el.setAttribute('data-tone', tone || 'info');
        el.hidden = false;
      } else {
        el.removeAttribute('data-tone');
        el.hidden = true;
      }
    });
  }

  /* The chips are rebuilt from the cart rather than mutated in place: the cart is the truth and a chip
     list edited by hand drifts from it the moment Shopify drops a code on its own. */
  function renderCodes(cart) {
    var codes = (cart && cart.discount_codes) || [];
    var applicable = [];
    var i;
    for (i = 0; i < codes.length; i += 1) {
      if (codes[i].applicable) {
        applicable.push(codes[i].code);
      }
    }

    each('[data-cart-promo-codes]', function (list) {
      list.innerHTML = '';
      if (!applicable.length) {
        list.hidden = true;
        return;
      }
      list.hidden = false;
      applicable.forEach(function (code) {
        var li = document.createElement('li');
        li.className = 'cart-promo__code';

        var label = document.createElement('span');
        label.className = 'cart-promo__code__label';
        label.textContent = code;
        li.appendChild(label);

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-promo__code__remove';
        btn.setAttribute('data-cart-promo-remove', code);
        /* The visible glyph is decorative; the accessible name carries the code, so a screen-reader user
           hears which of several codes this button removes. */
        btn.setAttribute('aria-label', t('remove', 'Remove [code]', { code: code }));
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
          '<path d="M18 6 6 18M6 6l12 12"></path></svg>';
        li.appendChild(btn);

        list.appendChild(li);
      });
    });
  }

  /*
   * TENIR « PASSER COMMANDE » PENDANT QU'UNE LIGNE CADEAU EST EN VOL.
   *
   * Mesuré sur ce thème — code FDGELYEUX, panier à 174,25 EUR, /cart.js relu toutes les 50 ms :
   *
   *   t=0     clic sur « Appliquer »
   *   t=251   le code est écrit ; Shopify le rend applicable:false
   *   t=501   addGiftLine() a posé GEL YEUX BLEUET à 20,00 EUR (propriété _promo_gift)
   *   t=902   le code a quitté le panier — la ligne n'est plus payée par rien
   *   t=1102  dropGift() la retire
   *
   * ~600 ms pendant lesquels le panier SERVEUR vaut 194,25 EUR au lieu de 174,25 et le bouton
   * « Passer commande » reste cliquable : `disabled === false` à chaque échantillon. Vérifié au
   * navigateur, un clic dans cette fenêtre crée une vraie session de checkout contenant un produit à
   * 20 EUR que le visiteur n'a jamais choisi et que l'interface lui présente comme offert.
   * verifyGiftFree() et dropGift() ferment ce risque sur l'ÉTAT FINAL du panier ; elles ne peuvent
   * rien pour un checkout parti au milieu.
   *
   * CE GARDE N'EST PAS LÀ POUR LE CODE PROMO, et c'est la correction à apporter à l'audit qui l'a
   * signalé : le tunnel revalide les codes tout seul, un code applicable:false n'y remise rien, et la
   * mention sous le bouton le dit déjà. Il est là pour la LIGNE.
   *
   * On ne relève que ce qu'on a soi-même abaissé (data-dc-promo-held) : un bouton que le thème tenait
   * déjà désactivé pour sa propre raison doit le rester.
   *
   * Le chien de garde est obligatoire, pas décoratif : fetch() n'a aucun délai d'expiration, donc une
   * requête qui ne revient jamais laisserait `busy` à true et le panier sans bouton de commande. Un
   * garde capable de condamner le checkout serait pire que pas de garde du tout.
   */
  var HOLD_MAX_MS = 12000;
  var holdWatchdog = null;

  function holdCheckout(state) {
    if (holdWatchdog) {
      clearTimeout(holdWatchdog);
      holdWatchdog = null;
    }
    each('button[name="checkout"]', function (b) {
      if (state) {
        if (b.disabled) {
          return; /* déjà tenu par le thème — ne pas s'en attribuer la levée */
        }
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
        b.setAttribute('data-dc-promo-held', '');
      } else if (b.hasAttribute('data-dc-promo-held')) {
        b.disabled = false;
        b.setAttribute('aria-busy', 'false');
        b.removeAttribute('data-dc-promo-held');
      }
    });
    if (state) {
      holdWatchdog = setTimeout(function () {
        /*
         * RENDRE LA MAIN SUR TOUT, PAS SEULEMENT SUR LE BOUTON.
         *
         * Le garde a deux serrures : l'attribut `disabled` posé ci-dessus, et le drapeau `busy` que
         * lit le gardien de soumission en phase de capture (voir init()). Une version antérieure de
         * ce chien de garde ne rouvrait que la première. Le résultat, si un fetch ne revient jamais
         * (réseau mobile qui décroche, portail captif) : passé HOLD_MAX_MS le bouton « Passer
         * commande » reprend son apparence normale — plein contraste, curseur pointeur, aucun
         * aria-busy — et chaque clic est silencieusement annulé par le gardien, parce que `busy`
         * est resté à true et qu'aucune des deux chaînes (apply/remove) ne rappellera jamais
         * setBusy(false). Un bouton mort qui a l'air vivant : exactement le mode de panne que ce
         * chien de garde existe pour empêcher.
         *
         * setBusy(false) rouvre les deux serrures et réactive « Appliquer », sans quoi le visiteur
         * ne pourrait pas non plus retenter son code. `normalising` est remis à zéro pour la même
         * raison : il verrouille normaliseGifts() et sa chaîne est elle aussi sans expiration.
         */
        normalising = false;
        setBusy(false);
      }, HOLD_MAX_MS);
    }
  }

  function setBusy(state) {
    busy = state;
    each('[data-cart-promo-submit]', function (b) {
      b.disabled = state;
      b.setAttribute('aria-busy', state ? 'true' : 'false');
    });
    holdCheckout(state);
  }

  /* ---------------------------------------------------------------- events */

  /* Tell the rest of the theme. change first (the badges, the totals, the reward ladder all read
     detail.cart), then reload so the drawer or the page re-renders its lines. */
  function announce(cart, stash) {
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
    );
    if (stash) {
      try {
        sessionStorage.setItem(STASH, JSON.stringify(stash));
      } catch (e) {
        /* Private mode, or storage disabled. The drawer keeps its message either way; only the cart
           page's post-reload message is lost, which is a smaller loss than not reloading. */
      }
    }
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  function restoreStashed() {
    var raw;
    try {
      raw = sessionStorage.getItem(STASH);
      sessionStorage.removeItem(STASH);
    } catch (e) {
      return;
    }
    if (!raw) {
      return;
    }
    try {
      var m = JSON.parse(raw);
      message(m.text, m.tone, m.quiet);
    } catch (e) {
      /* nothing to show */
    }
  }

  /* ------------------------------------------------------------------ flow */

  /*
   * Lit la réponse de Shopify après une écriture de codes, et décide.
   *
   * Renvoie le panier si le code a pris, ou null s'il a échoué — dans ce cas elle a déjà remis les codes
   * d'avant et affiché le message. Trois issues :
   *
   *   1. le code a pris             -> le panier
   *   2. il n'a pas pris, et une règle lui attache un cadeau -> ON POSE LA LIGNE ET ON RÉESSAIE UNE FOIS
   *   3. il a pris mais a évincé un code qui marchait -> conflit de cumul, on remet tout comme avant
   *
   * Le cas 2 est ce qui fait marcher une remise « Achetez X, obtenez Y », qui n'est applicable que si le
   * produit offert est déjà au panier. `allowRetry` est remis à false sur la deuxième passe : un seul
   * essai, jamais de boucle.
   *
   * Si le réessai échoue aussi, la ligne posée pour l'essai est retirée. Le visiteur ne doit pas garder
   * un article plein tarif à cause d'un code refusé.
   */
  function settle(cart, entered, before, allowRetry) {
    var codes = cart.discount_codes || [];
    var mine = null;
    var lost = [];
    var i;
    for (i = 0; i < codes.length; i += 1) {
      var c = codes[i];
      if (norm(c.code) === entered) {
        mine = c;
      } else if (!c.applicable && before.indexOf(c.code) !== -1) {
        lost.push(c.code);
      }
    }

    /* Remettre le panier exactement dans son état d'avant : un code mort dans la liste occuperait un des
       cinq emplacements et serait réémis à chaque écriture suivante. */
    function restore(key, fallback, vars) {
      return setCodes(before).then(function (restored) {
        renderCodes(restored);
        message(t(key, fallback, vars), 'error');
        return null;
      });
    }

    if (!mine || !mine.applicable) {
      var rule = ruleFor(entered);
      if (allowRetry && rule && rule.variant_id) {
        return addGiftLine(rule, entered).then(function (g) {
          if (!(g.added || g.already)) {
            return restore('invalid', '"[code]" is not valid for this cart.', { code: entered });
          }
          return setCodes(before.concat([entered]))
            .then(function (second) {
              return settle(second, entered, before, false);
            })
            .then(
              function (out) {
                if (out) {
                  return out;
                }
                /* settle() a déjà remis les codes et affiché le message ; reste la ligne de l'essai. */
                return dropGift(entered).then(function () {
                  return null;
                });
              },
              /*
               * LE RETRAIT DOIT AUSSI AVOIR LIEU QUAND LA CHAÎNE CASSE, pas seulement quand elle aboutit.
               *
               * La ligne cadeau est posée AVANT ce maillon. Si le réseau tombe ou si Shopify refuse
               * l'écriture, la branche de succès ci-dessus ne s'exécute jamais : le panier gardait alors un
               * article à plein tarif étiqueté cadeau — et depuis que ces lignes sont verrouillées, sans
               * prix, sans quantité et sans poubelle, le client ne pouvait plus s'en défaire. Il le payait.
               *
               * Ce chemin est devenu PLUS atteignable le jour où post() s'est mis à rejeter les réponses
               * d'erreur au lieu de les avaler : la correction d'un défaut a rendu l'autre visible.
               *
               * On retire, puis on relance l'erreur — apply() affichera son message générique, ce qui est
               * la bonne chose à dire quand on ignore ce qui s'est passé.
               */
              function (err) {
                return dropGift(entered).then(
                  function () {
                    throw err;
                  },
                  function () {
                    throw err;
                  }
                );
              }
            );
        });
      }
      return restore('invalid', '"[code]" is not valid for this cart.', { code: entered });
    }

    /* Il a pris, mais Shopify a évincé un code qui marchait avant : leurs réglages de Combinaisons
       n'autorisent pas la paire. On nomme le perdant — « votre remise a baissé » sans explication est le
       pire résultat possible ici. */
    if (lost.length) {
      return restore('conflict', '[a] cannot be combined with [b].', {
        a: entered,
        b: lost.join(', '),
      });
    }

    return Promise.resolve(cart);
  }

  function apply(code) {
    var entered = norm(code);
    if (!entered || busy) {
      if (!entered) {
        message(t('empty', 'Enter a code.'), 'error');
      }
      return;
    }

    setBusy(true);
    message('', null);

    var before = [];

    getCart()
      .then(function (cart) {
        before = liveCodes(cart);

        if (before.indexOf(entered) !== -1) {
          throw softError(t('already', 'That code is already applied.'));
        }
        if (before.length >= MAX_CODES) {
          throw softError(t('max', 'You can use up to [n] codes.', { n: MAX_CODES }));
        }

        /* The per-pair rule the merchant wrote, checked before Shopify sees the code. This is the one
           piece of arbitration Shopify cannot express (see the header note). It is advisory: a
           ?discount= link reaches the cart without passing through this file. */
        if (rules().enforce_pairs) {
          var blocked = firstConflict(entered, before);
          if (blocked) {
            throw softError(t('conflict', '[a] cannot be combined with [b].', { a: entered, b: blocked }));
          }
        }

        return setCodes(before.concat([entered]));
      })
      .then(function (cart) {
        return settle(cart, entered, before, true);
      })
      .then(function (cart) {
        if (!cart) {
          return null;
        }
        var rule = ruleFor(entered);
        if (!rule) {
          return { cart: cart, gift: null };
        }
        /* La ligne est peut-être déjà posée par le réessai de settle() ; addGiftLine le voit et ne
           double pas. La vérification, elle, tourne dans tous les cas — c'est elle qui protège. */
        return addGiftLine(rule, entered)
          .then(function (g) {
            return verifyGiftFree(rule, entered).then(function (v) {
              var gift = {
                added: g.added === true || g.already === true,
                unavailable: g.unavailable === true,
              };
              if (v.absent) {
                gift.added = false;
              }
              if (!v.ok) {
                gift.added = false;
                gift.not_free = true;
              }
              return gift;
            });
          })
          .then(function (gift) {
            return getCart().then(function (fresh) {
              return { cart: fresh, gift: gift, rule: rule };
            });
          });
      })
      .then(function (res) {
        if (!res) {
          return;
        }
        var text = t('applied', '[code] applied.', { code: entered });
        var tone = 'success';
        /* Le cas ordinaire est silencieux : la pastille porte déjà le code. Chaque branche qui apprend
           quelque chose de neuf remet quiet à false. */
        var quiet = true;

        if (res.gift) {
          if (res.gift.added) {
            text = t('applied_gift', '[code] applied — your gift is in the cart.', { code: entered });
            quiet = false;
          } else if (res.gift.not_free) {
            text = t(
              'gift_not_free',
              '[code] applied, but the gift could not be offered at no charge — it was not added.',
              { code: entered }
            );
            tone = 'warning';
            quiet = false;
          } else if (res.gift.unavailable) {
            text = t('gift_unavailable', '[code] applied, but the gift is out of stock.', {
              code: entered,
            });
            tone = 'warning';
            quiet = false;
          }
        }

        renderCodes(res.cart);
        message(text, tone, quiet);
        each('[data-cart-promo-input]', function (i) {
          i.value = '';
        });
        /* Liberer le verrou AVANT d'annoncer. normaliseGifts() se retire tant que `busy` est vrai, et
           announce() part de l'interieur de la chaine : le panier que cette operation vient de produire
           etait donc le seul que les gardiens ne voyaient jamais. Dans le drawer, aucun rechargement ne
           venait rattraper ca. */
        setBusy(false);
        announce(res.cart, { text: text, tone: tone, quiet: quiet });
      })
      .catch(function (err) {
        if (err && err.soft) {
          message(err.soft, 'error');
          return;
        }
        message(t('error', 'Something went wrong. Please try again.'), 'error');
      })
      .then(function () {
        setBusy(false);
      });
  }

  /* The merchant's per-pair rules. `stacks_with` is a whitelist when it is filled in and means "nothing
     else"; an empty value means the code has no opinion and only Shopify arbitrates. */
  function firstConflict(entered, existing) {
    var rule = ruleFor(entered);
    var i;
    for (i = 0; i < existing.length; i += 1) {
      var other = norm(existing[i]);
      if (rule && rule.stacks_with && rule.stacks_with.length) {
        if (rule.stacks_with.indexOf(other) === -1) {
          return existing[i];
        }
      }
      var otherRule = ruleFor(other);
      if (otherRule && otherRule.stacks_with && otherRule.stacks_with.length) {
        if (otherRule.stacks_with.indexOf(entered) === -1) {
          return existing[i];
        }
      }
    }
    return null;
  }

  function remove(code) {
    if (busy) {
      return;
    }
    var target = norm(code);
    setBusy(true);
    message('', null);

    getCart()
      .then(function (cart) {
        var keep = liveCodes(cart).filter(function (c) {
          return norm(c) !== target;
        });
        return setCodes(keep);
      })
      /* The gift goes out with the code that paid for it — otherwise the visitor keeps a product that is
         now full price, in a line they cannot edit. */
      .then(function () {
        return ruleFor(target) ? dropGift(target) : null;
      })
      .then(getCart)
      .then(function (cart) {
        renderCodes(cart);
        var text = t('removed', '[code] removed.', { code: target });
        message(text, 'info', true);
        setBusy(false); /* idem : les gardiens doivent voir ce panier-la (voir apply()) */
        announce(cart, { text: text, tone: 'info', quiet: true });
      })
      .catch(function () {
        message(t('error', 'Something went wrong. Please try again.'), 'error');
      })
      .then(function () {
        setBusy(false);
      });
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    /*
     * NO EARLY RETURN ON "[data-cart-promo] IS NOT THERE YET".
     *
     * An earlier version bailed out here when the field was absent, which looked like the usual
     * no-op-without-its-markup guard every script in this theme has. It was wrong, and it cost a debug
     * session: on the cart page the field is absent at the moment a deferred script runs and appears
     * shortly after (the theme re-renders the cart footer), so init() bound nothing, the submit was
     * never intercepted, and the browser natively posted the form — two navigations to /cart and a cart
     * with no code on it.
     *
     * The guard was also pointless: both listeners below are DELEGATED on document and match on a
     * selector, so they already cost nothing on a page that has no field and already survive the markup
     * being replaced. The config is read lazily for the same reason — the <script type="application/json">
     * that carries it comes from a different section and need not have been parsed yet.
     */

    /* Delegated on document: the drawer footer is re-rendered by the theme on every cart change, and the
       cart page is replaced wholesale on reload. A listener held on the form itself would be dropped
       with the node. */
    /* Click, not submit: the markup is deliberately not a <form> (see the note at the top of
       snippets/cart-promo-form.liquid — it would be nested inside the theme's cart form and the parser
       would drop it), so there is no submit event to listen for. */
    document.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest
        ? event.target.closest('[data-cart-promo-submit]')
        : null;
      if (!btn) {
        return;
      }
      event.preventDefault();
      var root = btn.closest('[data-cart-promo]');
      var input = root ? root.querySelector('[data-cart-promo-input]') : null;
      apply(input ? input.value : '');
    });

    /* Enter has to be wired by hand for the same reason, and it must not be allowed to bubble: the
       theme's cart form is an ancestor, and an un-prevented Enter in a field inside a form submits that
       form — which is exactly the accidental navigation the missing <form> caused in the first place. */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') {
        return;
      }
      var input = event.target && event.target.closest
        ? event.target.closest('[data-cart-promo-input]')
        : null;
      if (!input) {
        return;
      }
      event.preventDefault();
      apply(input.value);
    });

    document.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest
        ? event.target.closest('[data-cart-promo-remove]')
        : null;
      if (!btn) {
        return;
      }
      event.preventDefault();
      remove(btn.getAttribute('data-cart-promo-remove'));
    });

    /* Paint the chips from whatever the cart already holds — a code applied on a previous page, or one
       that arrived through a ?discount= link. */
    /* Only worth a request where the field actually exists; everywhere else the listeners simply sit
       there matching nothing. */
    if (document.querySelector('[data-cart-promo]')) {
      getCart()
        .then(function (cart) {
          renderCodes(cart);
          /* Au chargement aussi : un panier abîmé lors d'une visite précédente doit être remis d'aplomb
             avant que le visiteur ne le voie, pas seulement après sa prochaine action. */
          normaliseGifts(cart);
          return cart;
        })
        .catch(function () {
          /* The chips are a convenience; a failed cart fetch must not take the field down with it. */
          return null;
        });
    }

    /* Ceinture et bretelles. Un bouton `disabled` ne peut être ni cliqué ni servir de soumetteur
       implicite — vérifié au navigateur : avec disabled=true, .click() ne produit rien, et un
       form.requestSubmit(bouton) forcé n'envoie même plus `name=checkout`. Reste requestSubmit()
       appelé sur le FORMULAIRE lui-même, qui passe par l'événement submit et non par un clic. En
       phase de CAPTURE, donc avant tout gestionnaire du thème. */
    document.addEventListener(
      'submit',
      function (event) {
        if (!busy) {
          return;
        }
        var form = event.target;
        if (!(form && form.querySelector && form.querySelector('button[name="checkout"]'))) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    restoreStashed();

    /* The drawer's footer is rebuilt on every cart change, so the chips have to be repainted after it.
       Guarded against our own announce() by reading the cart from the event rather than re-fetching. */
    document.addEventListener('theme:cart:change', function (event) {
      if (event.detail && event.detail.cart) {
        renderCodes(event.detail.cart);
        normaliseGifts(event.detail.cart);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
